// Shared memory layout for handing render state from the sim worker to the main
// (render) thread with zero serialization. The worker writes agent snapshots
// into `data`; the renderer reads them each frame. When SharedArrayBuffer is
// available (cross-origin isolated) both threads view the same memory live;
// otherwise the worker posts copies (see simWorker/main fallback).

export const MAX_AGENTS = 40000; // render cap; excess agents simulate but aren't drawn
export const STRIDE = 11; // floats per agent

// ---------------------------------------------------------------------------
// Field offsets within an agent's stride.
//
// APPEND-ONLY. Existing offsets never move, so a writer that only knows the
// first N fields stays correct — the trailing floats simply stay 0. Every field
// below documents what 0 means, and the renderer treats 0 as "not populated"
// wherever that is ambiguous, so a partially-wired writer degrades to the old
// look rather than drawing nonsense.
// ---------------------------------------------------------------------------
export const F_ID = 0;
export const F_X = 1;
export const F_Y = 2;
export const F_SPECIES = 3;
export const F_ENERGY = 4; // energy fraction 0..1
export const F_SIZE = 5; // dimensionless body scale (pheno.size); 1.0 ≈ 45 kg
export const F_HEADING = 6; // radians; atan2(vy,vx)

/**
 * Behavioural state — `Agent.activity` as an integer.
 *
 *   0 = forage   working a patch: short steps, head down
 *   1 = rest     bedded down; drawn low, curled, legless and dimmed
 *   2 = travel   directed movement; drawn stretched along its heading
 *
 * 0 is also the "not populated" value, and it maps to forage, which is the
 * neutral upright silhouette — so an unwired writer looks exactly like the
 * previous renderer rather than parking the whole map in one odd pose.
 */
export const F_ACTIVITY = 7;

/**
 * age / lifespan, 0..1. Drives juvenile scaling: below ~0.15 the animal is
 * drawn smaller (a fawn beside its mother is one of the clearest signals that
 * this is a living population and not a particle field).
 *
 * 0 means NOT POPULATED and is drawn at full adult size. A genuinely newborn
 * animal is also ~0 for a fraction of a second, which is harmless.
 */
export const F_AGE_FRAC = 8;

/**
 * |vel| in ABSOLUTE m/s. Adds a motion streak and body elongation, which is what
 * makes a chase read as a chase.
 *
 * Absolute rather than a fraction of the animal's own maximum, deliberately: a
 * slow animal flat out should not streak like a sprinting one, because the
 * viewer is judging speed across the whole screen, not per animal. The renderer
 * divides by SPEED_REF to normalise. (This field was originally specified as a
 * 0..1 fraction and written as m/s, which multiplied every speed-driven effect
 * by ~20 — streaks became scratches across the map and runners became slivers.)
 *
 * 0 = not populated or genuinely stationary; both draw with no streak.
 */
export const F_SPEED = 9;

/**
 * Sex: 1 = male, 0 = female (and 0 when not populated).
 *
 * Drawn only as ANTLERS on mature male deer — the single most recognisable
 * thing about the species, and honest, because only the males carry them. A
 * female or a fawn simply has none, so an unwired writer draws every deer
 * antlerless rather than drawing something false.
 */
export const F_SEX = 10;

/** Values for F_ACTIVITY, mirroring the `Activity` union in sim/agent.ts. */
export const ACT_FORAGE = 0;
export const ACT_REST = 1;
export const ACT_TRAVEL = 2;

// Control block (Int32). COUNT is written last (after data) so a reader that
// sees the new count also sees the corresponding data.
export const CTRL_LEN = 4;
export const C_COUNT = 0;
export const C_SEQ = 1;

export const DATA_FLOATS = MAX_AGENTS * STRIDE;

export interface RawBuffers {
  ctrlBuf: ArrayBufferLike;
  dataBuf: ArrayBufferLike;
}

export function canUseShared(): boolean {
  return (
    typeof SharedArrayBuffer !== "undefined" &&
    typeof globalThis.crossOriginIsolated !== "undefined" &&
    globalThis.crossOriginIsolated === true
  );
}

export function allocBuffers(shared: boolean): RawBuffers {
  if (shared) {
    return {
      ctrlBuf: new SharedArrayBuffer(CTRL_LEN * 4),
      dataBuf: new SharedArrayBuffer(DATA_FLOATS * 4),
    };
  }
  return {
    ctrlBuf: new ArrayBuffer(CTRL_LEN * 4),
    dataBuf: new ArrayBuffer(DATA_FLOATS * 4),
  };
}
